import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';

import { fetchEmployeeDetailsByEmployeeId } from '~/entities/Employee/api/employee.api';
import { GET_EMPLOYEE_DETAILS_QUERY_KEY } from '~/entities/Employee/api/useEmployeeDetails';
import { useGetEmployeeRole } from '~/entities/RoleSetting/api/useGetEmployeeRole';
import { User, useUser } from '~/shared/auth/useUser';
import { EAnalyticsEvents } from '~/shared/enums';
import { GA } from '~/shared/lib';
import { mixPanel } from '~/shared/lib/mixpanel';

export function useEventsAnalytics() {
  const user = useUser();
  const { data: employeeRole } = useGetEmployeeRole(user.employeeId || '');
  const { data: employeeDetails } = useQuery({
    queryKey: [GET_EMPLOYEE_DETAILS_QUERY_KEY, user.employeeId],
    queryFn: () => fetchEmployeeDetailsByEmployeeId(user.employeeId!),
    enabled: !!user.employeeId,
    staleTime: 1000 * 60 * 5,
  });

  const fireClickCoiAnalyticsEvent = useCallback(
    (u: User) => {
      GA.fireEvent({
        action: 'click_coi',
        category: 'Navigation',
        label: 'Click COI',
        value: 1,
        user: u,
        employeeRole,
        employeeDetails,
      });
    },
    [employeeRole, employeeDetails]
  );

  const fireEventPayload = {
    employeeRole,
    employeeDetails,
  };

  const fireClickGdAnalyticsEvent = useCallback(
    (u: User) => {
      GA.fireEvent({
        action: 'click_gd',
        category: 'Navigation',
        label: 'Click GD',
        value: 1,
        user: u,
        ...fireEventPayload,
      });
    },
    [employeeRole, employeeDetails]
  );

  const fireClickOcmAnalyticsEvent = useCallback(
    (u: User) => {
      GA.fireEvent({
        action: 'click_ocm',
        category: 'Navigation',
        label: 'Click OCM',
        value: 1,
        user: u,
        ...fireEventPayload,
      });
    },
    [employeeRole, employeeDetails]
  );

  const fireClickEmployeeMovementsAnalyticsEvent = useCallback(
    (u: User) => {
      GA.fireEvent({
        action: 'click_employee_movements',
        category: 'Navigation',
        label: 'Click Employee Movements',
        value: 1,
        user: u,
        ...fireEventPayload,
      });
    },
    [employeeRole, employeeDetails]
  );

  const fireClickCancelAnalyticsEvent = useCallback(
    (u: User) => {
      GA.fireEvent({
        action: 'click_cancel',
        category: 'Action',
        label: 'Click Cancel',
        value: 1,
        user: u,
        ...fireEventPayload,
      });
    },
    [employeeRole, employeeDetails]
  );

  const fireClickGlobalPoliciesAnalyticsEvent = useCallback(
    (u: User) => {
      GA.fireEvent({
        action: 'click_global_policies',
        category: 'Navigation',
        label: 'Click Global Policies',
        value: 1,
        user: u,
        ...fireEventPayload,
      });
      mixPanel.fireEvent({
        event: EAnalyticsEvents.CLICK_SIDEBAR_GLOBAL_POLICIES,
      });
    },
    [employeeRole, employeeDetails]
  );

  const fireClickGlobalTrainingsAnalyticsEvent = useCallback(
    (u: User) => {
      GA.fireEvent({
        action: 'click_global_trainings',
        category: 'Navigation',
        label: 'Click Global Trainings',
        value: 1,
        user: u,
        ...fireEventPayload,
      });
      mixPanel.fireEvent({
        event: EAnalyticsEvents.CLICK_SIDEBAR_GLOBAL_TRAININGS,
      });
    },
    [employeeRole, employeeDetails]
  );

  const fireClickHelpLineAnalyticsEvent = useCallback(
    (u: User) => {
      GA.fireEvent({
        action: 'click_help_line',
        category: 'Navigation',
        label: 'Click Help Line',
        value: 1,
        user: u,
        ...fireEventPayload,
      });
      mixPanel.fireEvent({
        event: EAnalyticsEvents.CLICK_SIDEBAR_HELP_LINE,
      });
    },
    [employeeRole, employeeDetails]
  );

  const fireClickAccessHelplineAnalyticsEvent = useCallback(
    (u: User) => {
      GA.fireEvent({
        action: 'click_access_helpline',
        category: 'Navigation',
        label: 'Click Access Helpline',
        value: 1,
        user: u,
        ...fireEventPayload,
      });
    },
    [employeeRole, employeeDetails]
  );

  const fireClickFilterCategoryAnalyticsEvent = useCallback(
    (u: User) => {
      GA.fireEvent({
        action: 'click_filter_category',
        category: 'Filter',
        label: 'Click Filter Category',
        value: 1,
        user: u,
        ...fireEventPayload,
      });
    },
    [employeeRole, employeeDetails]
  );

  const fireClickFilterZoneAnalyticsEvent = useCallback(
    (u: User) => {
      GA.fireEvent({
        action: 'click_filter_zone',
        category: 'Filter',
        label: 'Click Filter Zone',
        value: 1,
        user: u,
        ...fireEventPayload,
      });
      mixPanel.fireEvent({
        event: EAnalyticsEvents.CLICK_FILTER_ZONE_MY_TICKETS,
      });
    },
    [employeeRole, employeeDetails]
  );

  const fireClickFilterStatusAnalyticsEvent = useCallback(
    (u: User) => {
      GA.fireEvent({
        action: 'click_filter_status',
        category: 'Filter',
        label: 'Click Filter Status',
        value: 1,
        user: u,
        ...fireEventPayload,
      });
      mixPanel.fireEvent({
        event: EAnalyticsEvents.CLICK_FILTER_STATUS_MY_TICKETS,
      });
    },
    [employeeRole, employeeDetails]
  );

  const fireClickFilterBuAnalyticsEvent = useCallback(
    (u: User) => {
      GA.fireEvent({
        action: 'click_filter_bu',
        category: 'Filter',
        label: 'Click Filter BU',
        value: 1,
        user: u,
        ...fireEventPayload,
      });
      mixPanel.fireEvent({
        event: EAnalyticsEvents.CLICK_FILTER_BU_MY_TICKETS,
      });
    },
    [employeeRole, employeeDetails]
  );

  const fireClickFilterCreatedDateAnalyticsEvent = useCallback(
    (u: User) => {
      GA.fireEvent({
        action: 'click_filter_created_date',
        category: 'Filter',
        label: 'Click Filter Created Date',
        value: 1,
        user: u,
        ...fireEventPayload,
      });
      mixPanel.fireEvent({
        event: EAnalyticsEvents.CLICK_FILTER_DATE_MY_TICKETS,
      });
    },
    [employeeRole, employeeDetails]
  );

  const fireClickFilterEndDateAnalyticsEvent = useCallback(
    (u: User) => {
      GA.fireEvent({
        action: 'click_filter_end_date',
        category: 'Filter',
        label: 'Click Filter End Date',
        value: 1,
        user: u,
        ...fireEventPayload,
      });
      mixPanel.fireEvent({
        event: EAnalyticsEvents.CLICK_FILTER_DATE_MY_TICKETS,
      });
    },
    [employeeRole, employeeDetails]
  );

  const fireClickCardsViewAnalyticsEvent = useCallback(
    (u: User) => {
      GA.fireEvent({
        action: 'click_cards_view',
        category: 'View',
        label: 'Click Cards View',
        value: 1,
        user: u,
        ...fireEventPayload,
      });
    },
    [employeeRole, employeeDetails]
  );

  const fireClickEditAnalyticsEvent = useCallback(
    (u: User) => {
      GA.fireEvent({
        action: 'click_edit',
        category: 'Action',
        label: 'Click Edit',
        value: 1,
        user: u,
        ...fireEventPayload,
      });
    },
    [employeeRole, employeeDetails]
  );

  const fireClickAssignAnalyticsEvent = useCallback(
    (u: User) => {
      GA.fireEvent({
        action: 'click_assign',
        category: 'Action',
        label: 'Click Assign',
        value: 1,
        user: u,
        ...fireEventPayload,
      });
    },
    [employeeRole, employeeDetails]
  );

  const fireClickChatAnalyticsEvent = useCallback(
    (u: User) => {
      GA.fireEvent({
        action: 'click_chat',
        category: 'Action',
        label: 'Click Chat',
        value: 1,
        user: u,
        ...fireEventPayload,
      });
    },
    [employeeRole, employeeDetails]
  );

  const fireClickApprovalAnalyticsEvent = useCallback(
    (u: User) => {
      GA.fireEvent({
        action: 'click_approval',
        category: 'Action',
        label: 'Click Approval',
        value: 1,
        user: u,
        ...fireEventPayload,
      });
    },
    [employeeRole, employeeDetails]
  );

  const fireClickTicketActivityAnalyticsEvent = useCallback(
    (u: User) => {
      GA.fireEvent({
        action: 'click_ticket_activity',
        category: 'Action',
        label: 'Click Ticket Activity',
        value: 1,
        user: u,
        ...fireEventPayload,
      });
    },
    [employeeRole, employeeDetails]
  );

  return {
    fireClickCoiAnalyticsEvent,
    fireClickGdAnalyticsEvent,
    fireClickOcmAnalyticsEvent,
    fireClickEmployeeMovementsAnalyticsEvent,
    fireClickCancelAnalyticsEvent,
    fireClickGlobalPoliciesAnalyticsEvent,
    fireClickGlobalTrainingsAnalyticsEvent,
    fireClickHelpLineAnalyticsEvent,
    fireClickAccessHelplineAnalyticsEvent,
    fireClickFilterCategoryAnalyticsEvent,
    fireClickFilterZoneAnalyticsEvent,
    fireClickFilterStatusAnalyticsEvent,
    fireClickFilterBuAnalyticsEvent,
    fireClickFilterCreatedDateAnalyticsEvent,
    fireClickFilterEndDateAnalyticsEvent,
    fireClickCardsViewAnalyticsEvent,
    fireClickEditAnalyticsEvent,
    fireClickAssignAnalyticsEvent,
    fireClickChatAnalyticsEvent,
    fireClickApprovalAnalyticsEvent,
    fireClickTicketActivityAnalyticsEvent,
  };
}
